import { DatePicker, Form, FormInstance, Input, Tooltip } from 'antd';
import FormLabel from 'components/FormLabel';
import InformationBanner from 'components/InformationBanner';
import { requiredRule } from 'const/formRules';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NouveauDossierConcessionnaireData, RAI } from 'types/Concessionnaire';
import FilesUpload from 'views/dossiers/concessionnaire/steps/components/FilesUpload';
import {
  dateDevisRules,
  getDatePrevisionnelleDevisRules,
  reverseFieldOrderClassName,
} from 'views/dossiers/concessionnaire/steps/step2/step2.rules';

interface Step2CommandeSectionFieldsProps {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  rai?: RAI;
  personnePhysique: boolean;
  onDevisChange?: (files?: NouveauDossierConcessionnaireData['devis']) => void;
  onOtherFilesChange?: (
    files?: NouveauDossierConcessionnaireData['otherFiles'],
  ) => void;
}

export default function Step2CommandeSectionFields({
  form,
  rai,
  personnePhysique,
  onDevisChange,
  onOtherFilesChange,
}: Step2CommandeSectionFieldsProps) {
  const { t } = useTranslation();

  return (
    <>
      <Form.Item<NouveauDossierConcessionnaireData>
        label={
          <FormLabel>
            {t('nouveau_formulaire:nbDevis:label')}
            <Tooltip title={t('nouveau_formulaire:nbDevis:tip')}>
              <span className="absolute left-[100%] top-0">
                <InformationBanner />
              </span>
            </Tooltip>
          </FormLabel>
        }
        name="nbDevis"
        required
        rules={[requiredRule]}
      >
        <Input
          placeholder={t('nouveau_formulaire:nbDevis:placeholder')}
          className="md:w-1/2"
        />
      </Form.Item>

      {personnePhysique ? (
        // Pour personne physique : toujours afficher "Date d'edition du bon de commande"
        <Form.Item<NouveauDossierConcessionnaireData>
          label={t('nouveau_formulaire:date_edition_devis:label')}
          name="dateEditionDevis"
          rules={dateDevisRules['3.2']}
          required
          className={reverseFieldOrderClassName}
        >
          <DatePicker
            className="md:w-1/3"
            format="DD/MM/YYYY"
            placeholder={t('nouveau_formulaire:date_edition_devis:placeholder')}
          />
        </Form.Item>
      ) : rai === '3.1' ? (
        // Pour personne morale RAI 3.1 : "Date de signature du bon de commande"
        <Form.Item<NouveauDossierConcessionnaireData>
          extra={
            <InformationBanner className="font-normal mt-2">
              {t('nouveau_formulaire:date_devis:banner')}
            </InformationBanner>
          }
          label={t('nouveau_formulaire:date_devis:label')}
          name="dateDevis"
          rules={dateDevisRules[rai]}
          required
          className={reverseFieldOrderClassName}
        >
          <DatePicker
            className="md:w-1/3"
            format="DD/MM/YYYY"
            placeholder={t('nouveau_formulaire:date_devis:placeholder')}
          />
        </Form.Item>
      ) : (
        // Pour personne morale RAI 3.2 : "Date d'edition du bon de commande"
        <Form.Item<NouveauDossierConcessionnaireData>
          label={t('nouveau_formulaire:date_edition_devis:label')}
          name="dateEditionDevis"
          rules={rai ? dateDevisRules[rai] : undefined}
          required
          className={reverseFieldOrderClassName}
        >
          <DatePicker
            className="md:w-1/3"
            format="DD/MM/YYYY"
            placeholder={t('nouveau_formulaire:date_edition_devis:placeholder')}
          />
        </Form.Item>
      )}

      {(personnePhysique || rai === '3.2') && (
        // Pour personne physique : toujours afficher
        // Pour personne morale : afficher uniquement si RAI 3.2
        <Form.Item<NouveauDossierConcessionnaireData>
          label={t('nouveau_formulaire:date_previsionnelle_devis:label')}
          name="datePrevisionnelleDevis"
          rules={getDatePrevisionnelleDevisRules(personnePhysique, form)}
          className={reverseFieldOrderClassName}
        >
          <DatePicker
            className="md:w-1/3"
            format="DD/MM/YYYY"
            placeholder={t(
              'nouveau_formulaire:date_previsionnelle_devis:placeholder',
            )}
          />
        </Form.Item>
      )}

      <FilesUpload
        onDevisChange={onDevisChange}
        onOtherFilesChange={onOtherFilesChange}
      />
    </>
  );
}
